/**
 * Confirms the exact scope of an administrative user-removal operation.
 *
 * Workspace membership removal and account deletion have different effects.
 * Centralized scope-aware wording prevents the UI from implying broader data
 * loss; the parent action and backend remain the authorization boundary.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui';
import { Trash2 } from 'lucide-react';

type UserRemovalScope = 'all' | 'organization' | 'workspace';

interface UserRemovalDialogProps {
  userId: string | null;
  scope: UserRemovalScope;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

export function UserRemovalDialog({
  userId,
  scope,
  isPending,
  onOpenChange,
  onConfirm,
}: UserRemovalDialogProps) {
  const workspace = scope === 'workspace';
  const title = workspace ? 'Remove from workspace?' : 'Delete user?';
  const action = workspace ? 'Remove member' : 'Delete user';

  return (
    <AlertDialog open={userId !== null} onOpenChange={onOpenChange}>
      <AlertDialogContent className="block w-[440px] gap-0 rounded-2xl border-0 bg-white p-[26px] shadow-[0_24px_64px_rgba(2,8,23,0.32)] sm:max-w-[440px]">
        <AlertDialogHeader className="flex-row items-start gap-[14px] text-left">
          <div className="flex size-[42px] shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-600">
            <Trash2 className="size-[18px]" />
          </div>
          <div className="flex min-w-0 flex-col gap-[5px] pt-px">
            <AlertDialogTitle className="text-[17px] font-bold leading-[normal] text-slate-900">
              {title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-[1.55] text-slate-500">
              Are you sure you want to {workspace ? 'remove' : 'delete'}{' '}
              <span className="font-semibold text-slate-900">{userId}</span>{' '}
              {scope === 'all' && 'from the system? Their account and access will be deleted.'}
              {scope === 'organization' && 'from the organization?'}
              {workspace && 'from this workspace? They will remain in the organization directory.'}
            </AlertDialogDescription>
          </div>
        </AlertDialogHeader>

        <AlertDialogFooter className="mt-6 flex-row justify-end gap-2.5">
          <AlertDialogCancel
            disabled={isPending}
            className="h-[38px] rounded-lg border-slate-200 px-4 text-[13.5px] font-[550] leading-[normal] text-slate-700 shadow-none hover:bg-slate-50"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            onClick={(event) => {
              event.preventDefault();
              onConfirm();
            }}
            className="h-[38px] gap-[7px] rounded-lg bg-red-600 px-[18px] text-[13.5px] font-semibold leading-[normal] text-white shadow-[0_1px_2px_rgba(220,38,38,0.3)] transition-[background,transform] duration-150 hover:bg-red-700 active:scale-[0.97] has-[>svg]:px-[18px]"
          >
            <Trash2 className="size-[15px]" />
            {isPending ? 'Working…' : action}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
